import React, { useMemo } from 'react'
import { motion } from 'framer-motion'

export interface OrderBookBarData {
  month: string // Short month name: 'Oct', 'Nov', etc.
  value: number // Value for the bar height (0-100 or actual value)
  isHighlighted?: boolean // If true, uses yellow-green color, otherwise gray
}

interface OrderBookGraphProps {
  data?: OrderBookBarData[]
  className?: string
}

/**
 * 🔴 There is no fallback data. There used to be.
 *
 * `DEFAULT_DATA` was a plausible six-month bar chart with real month labels
 * (`Oct 60 … Mar 90`), applied by a default parameter — so ANY caller
 * that passed nothing, or an empty array, rendered an invented trend beneath a
 * real headline figure. `OTCMetricsCards` passed `undefined` deliberately.
 * A chart with nothing to plot now renders nothing, and the slot keeps its
 * height so the card does not reflow.
 */
const OrderBookGraph: React.FC<OrderBookGraphProps> = ({
  data,
  className = ''
}) => {
  // Calculate normalized heights based on max value (Figma graph area height = 80px)
  const normalizedData = useMemo(() => {
    const points = data ?? []
    const maxValue = Math.max(...points.map(d => d.value), 1)
    return points.map(item => ({
      ...item,
      height: (item.value / maxValue) * 80
    }))
  }, [data])

  // Reverse data to match right-to-left order (Mar, Feb, Jan, Dec, Nov, Oct)
  const reversedData = [...normalizedData].reverse()

  if (reversedData.length === 0) {
    return <div className={`relative w-full max-w-[214px] h-[100px] ${className}`} aria-hidden="true" />
  }

  return (
    <div className={`relative w-full max-w-[214px] h-[100px] ${className}`}>
      {/* Bars - positioned absolutely from right to left */}
      {reversedData.map((item, index) => {
        const isHighlighted = item.isHighlighted ?? false
        
        // Left positions: Mar=180px, Feb=146px, Jan=112px, Dec=78px, Nov=44px, Oct=10px
        const leftPositions = [180, 146, 112, 78, 44, 10]
        const leftPosition = leftPositions[index]
        
        // Gradient colors based on highlight
        const gradientFrom = isHighlighted ? '#e3fd5a' : '#b1b0b0'
        const gradientVia = isHighlighted ? 'rgba(197,204,159,0.24)' : 'rgba(187,187,187,0.24)'
        
        // All columns have left border, first column (Mar, index 0) also has right border
        const borderStyle = '1px dashed rgba(45,41,35,0.32)'
        
        return (
          <div
            key={`${item.month}-${index}`}
            className="absolute top-0 w-[34px] h-[80px]"
            style={{
              left: `${leftPosition}px`,
              borderLeft: borderStyle,
              borderRight: index === 0 ? borderStyle : 'none', // Mar (first in reversed) has right border
            }}
          >
            {/* Gradient bar inside */}
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: item.height, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[18px]"
              style={{
                background: `linear-gradient(to bottom, ${gradientFrom} 19.712%, ${gradientVia} 77.404%, rgba(255,255,255,0) 100%)`,
              }}
            />
          </div>
        )
      })}
          
      {/* Month labels - positioned absolutely at bottom, below graph area */}
      {reversedData.map((item, index) => {
        // Right positions from design: Mar=17px, Feb=51px, Jan=85px, Dec=119px, Nov=153px, Oct=187px
        const rightPositions = [17, 51, 85, 119, 153, 187]
        const rightPosition = rightPositions[index]
        return (
          <p
            key={`label-${item.month}-${index}`}
            className="absolute top-[88px] w-[34px] font-plus-jakarta text-[9px] leading-3 text-center text-[rgba(45,41,35,0.6)] dark:text-[rgba(253,253,253,0.6)] font-normal"
            style={{
              right: `${rightPosition}px`,
              transform: 'translateX(50%)'
            }}
          >
            {item.month}
          </p>
        )
      })}
      
      {/* Average line */}
      <div className="absolute left-0 top-[45px] w-full h-px bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(253,253,253,0.72)]" />

      {/* Avg label */}
      <div className="absolute left-0 top-[35px] flex items-center justify-center py-1 px-1 bg-[#2d2923] dark:bg-[#fdfdfd]">
        <p className="text-center text-white dark:text-[#0a0a0a] text-[11px] leading-3 font-semibold">
          Avg
        </p>
      </div>
    </div>
  )
}

export default OrderBookGraph
